/**
 * Alcance de categorías de un catálogo (ticket #86).
 *
 * Permite que un mismo componente de catálogo, puesto en dos páginas distintas
 * del mismo sitio, muestre conjuntos distintos de vehículos: una página
 * "Camionetas" que sólo lista las categorías elegidas, y el catálogo principal
 * que las deja fuera.
 *
 * La configuración vive en las props del NODO del builder, no en el vehículo ni
 * en el cliente: no se toca la categoría de ningún auto y dos catálogos del
 * mismo sitio pueden estar acotados de forma diferente.
 *
 * Se filtra por `categories.id`, no por nombre: el catálogo global tiene
 * nombres que se pisan entre sí ("Sedan" y "Sedán", "Wagon" y "Station Wagon",
 * "Pickup" y "Camioneta" son filas distintas), y emparejar por texto haría que
 * elegir una arrastrara a la otra sin que el dealer lo pidiera.
 */

export type ModoAlcanceCategorias = 'all' | 'only' | 'except';

/** Vehículo visto por este módulo: sólo necesita saber su categoría. */
interface ConCategoria {
  category?: { id?: number | string | null } | null;
}

/**
 * Normaliza lo que venga guardado en el árbol a un conjunto de ids en texto.
 * Acepta arrays de números o strings y también un CSV, porque el árbol de un
 * sitio publicado es dato viejo: no hay forma de garantizar su forma.
 */
export function normalizarIdsDeCategoria(crudo: unknown): Set<string> {
  const ids = new Set<string>();
  const agregar = (v: unknown) => {
    if (v === null || v === undefined) return;
    const s = String(v).trim();
    if (s) ids.add(s);
  };

  if (Array.isArray(crudo)) {
    crudo.forEach(agregar);
  } else if (typeof crudo === 'string') {
    crudo.split(',').forEach(agregar);
  }
  return ids;
}

/**
 * `true` sólo si el nodo pide de verdad acotar. Un catálogo sin configurar
 * —el caso de todos los que ya están publicados— cae siempre acá en `false` y
 * sigue mostrando el inventario completo.
 */
export function alcanceEstaActivo(modo: unknown, idsCrudos: unknown): boolean {
  if (modo !== 'only' && modo !== 'except') return false;
  return normalizarIdsDeCategoria(idsCrudos).size > 0;
}

/**
 * Aplica el alcance sobre una lista de vehículos.
 *
 * Devuelve el MISMO array cuando no hay nada que acotar, para no romper las
 * comparaciones por referencia de los `useMemo` que lo consumen.
 *
 * En modo `only`, un vehículo sin categoría queda fuera (no se puede afirmar
 * que pertenezca a las elegidas); en modo `except` queda dentro (no se puede
 * afirmar que sea una de las excluidas).
 */
export function aplicarAlcanceDeCategorias<T extends ConCategoria>(
  vehiculos: T[],
  modo: unknown,
  idsCrudos: unknown
): T[] {
  if (!Array.isArray(vehiculos) || !alcanceEstaActivo(modo, idsCrudos)) return vehiculos;

  const ids = normalizarIdsDeCategoria(idsCrudos);
  const incluir = modo === 'only';

  return vehiculos.filter((v) => {
    const id = v?.category?.id;
    if (id === null || id === undefined || id === '') return !incluir;
    return ids.has(String(id)) === incluir;
  });
}

/**
 * Cómo pedirle al SERVIDOR el alcance, para que el filtro ocurra ANTES de
 * cualquier tope de filas.
 *
 * Existe por un bug real (ticket #86): la vista previa del builder pide los 50
 * vehículos más recientes del tenant y recién después filtraba en memoria. En
 * un tenant con historial esos 50 son casi todos autos vendidos, así que un
 * catálogo acotado a Pickup mostraba 2 de 9 y el panel decía otra cosa que la
 * grilla. Recortar una muestra ya truncada da resultados arbitrarios.
 *
 * Devuelve una descripción, no una consulta: así se puede probar sin red y el
 * que la usa la traduce a su cliente.
 */
export type FiltroDeCategoriaEnConsulta =
  | { tipo: 'ninguno' }
  | { tipo: 'in'; columna: 'category_id'; valores: string[] }
  | { tipo: 'or'; expresion: string };

export function filtroDeCategoriaParaConsulta(
  modo: unknown,
  idsCrudos: unknown
): FiltroDeCategoriaEnConsulta {
  if (!alcanceEstaActivo(modo, idsCrudos)) return { tipo: 'ninguno' };

  const ids = [...normalizarIdsDeCategoria(idsCrudos)];

  if (modo === 'only') {
    return { tipo: 'in', columna: 'category_id', valores: ids };
  }

  // `not.in` deja fuera los NULL, y un auto sin categoría no es una de las
  // excluidas: se conserva, igual que en `aplicarAlcanceDeCategorias`.
  return {
    tipo: 'or',
    expresion: `category_id.is.null,category_id.not.in.(${ids.join(',')})`,
  };
}

/**
 * Clave estable para las listas de dependencias: el array de ids llega como una
 * referencia nueva en cada render del editor, y usarlo directo en un `useMemo`
 * o en un `useEffect` que consulta la base dispararía el efecto sin parar.
 */
export function claveDeAlcance(modo: unknown, idsCrudos: unknown): string {
  if (!alcanceEstaActivo(modo, idsCrudos)) return 'all';
  return `${modo}:${[...normalizarIdsDeCategoria(idsCrudos)].sort().join(',')}`;
}

/**
 * Nombres de categoría que el catálogo tiene que OFRECER como pestaña aunque no
 * haya ni un vehículo publicado en ellas (ticket #97).
 *
 * Las pestañas se derivan del inventario que el catálogo trae: es lo correcto
 * por defecto —una pestaña que siempre devuelve cero es una promesa falsa— pero
 * deja al dealer sin ver lo que acaba de configurar. Marcar una categoría en
 * modo `only` es justamente decir "esta página es de esto", así que ahí la
 * elección explícita pesa más que la heurística.
 *
 * `except` no entra: ahí las elegidas son las que se esconden.
 *
 * El nombre viaja en las props del nodo junto a los ids (`categoryScopeNames`),
 * escrito por el panel cuando se marca la categoría. Evita que cada grilla
 * consulte `categories` sólo para poner un rótulo, y un árbol ya publicado que
 * no trae la prop se comporta igual que antes.
 */
export function nombresParaOfrecerComoPestana(
  modo: unknown,
  idsCrudos: unknown,
  nombresCrudos: unknown
): string[] {
  if (modo !== 'only' || !alcanceEstaActivo(modo, idsCrudos)) return [];
  if (!Array.isArray(nombresCrudos)) return [];

  const vistos = new Set<string>();
  for (const n of nombresCrudos) {
    if (typeof n !== 'string') continue;
    const limpio = n.trim();
    if (limpio) vistos.add(limpio);
  }
  return [...vistos];
}
